import { format, isValid } from 'date-fns';
import moment from 'moment';

// Черновик регистрации живёт, пока открыта вкладка: F5 и выгрузка вкладки
// на телефоне не сбрасывают заполненное. Состояние мастера и ввод шагов
// лежат в sessionStorage, файлы — в IndexedDB (в sessionStorage помещаются
// только строки, около 5 МБ). Без хранилища регистрация работает как раньше.

const DRAFT_KEY = 'registration-draft';
// поднять, если меняется форма черновика: старый тогда просто отбросится
const DRAFT_VERSION = 1;
const STEPS = ['main', 'contacts', 'vulnerable', 'special'];
// шаги с галочкой «пройден» (у последнего, подтверждения, её нет)
const COMPLETABLE_STEPS = 7;

export const FILE_KEYS = ['avatar_photo', 'document', 'inclusiveness_document'];
// шаг мастера, на котором загружается файл
const FILE_STEPS = { avatar_photo: 0, document: 2, inclusiveness_document: 5 };

const DB_NAME = 'bilemtihan-registration';
const STORE = 'files';
// столько живут файлы вкладки, закрытой без отправки заявки
const STALE_FILE_AGE = 2 * 60 * 60 * 1000;
// Safari 14 бывает подвешивает indexedDB.open — лоадер не должен висеть вечно
const RESTORE_TIMEOUT = 3000;

const isObject = (value) =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const isValidDraft = (draft) =>
  isObject(draft) &&
  draft.v === DRAFT_VERSION &&
  isObject(draft.body) &&
  isObject(draft.fileRefs) &&
  Number.isInteger(draft.selectedIndex) &&
  draft.selectedIndex >= 0 &&
  draft.selectedIndex <= COMPLETABLE_STEPS &&
  Array.isArray(draft.completed) &&
  draft.completed.length === COMPLETABLE_STEPS &&
  draft.completed.every((done) => typeof done === 'boolean');

export const loadDraft = () => {
  try {
    const draft = JSON.parse(sessionStorage.getItem(DRAFT_KEY));
    return isValidDraft(draft) ? draft : null;
  } catch (e) {
    return null;
  }
};

export const saveDraft = (draft) => {
  const body = { ...draft.body };
  // File в JSON не сохранить, сами файлы лежат в IndexedDB
  FILE_KEYS.forEach((key) => {
    body[key] = null;
  });
  try {
    sessionStorage.setItem(
      DRAFT_KEY,
      JSON.stringify({ ...draft, body, v: DRAFT_VERSION }),
    );
  } catch (e) {
    // нет sessionStorage или кончилась квота — живём без черновика
  }
};

const stepKey = (step) => `${DRAFT_KEY}:${step}`;

// Ввод шага, ещё не подтверждённый кнопкой «Сохранить».
export const readStepDraft = (step) => {
  try {
    const values = JSON.parse(sessionStorage.getItem(stepKey(step)));
    return isObject(values) ? values : null;
  } catch (e) {
    return null;
  }
};

export const writeStepDraft = (step, values) => {
  try {
    sessionStorage.setItem(stepKey(step), JSON.stringify(values));
  } catch (e) {
    // см. saveDraft
  }
};

export const clearStepDraft = (step) => {
  try {
    sessionStorage.removeItem(stepKey(step));
  } catch (e) {
    // см. saveDraft
  }
};

export const clearStepDrafts = () => STEPS.forEach(clearStepDraft);

// Дата в черновике шага хранится как body.birth_date, без часового пояса.
export const toDraftDate = (date) =>
  date instanceof Date && isValid(date)
    ? format(date, 'yyyy-MM-dd')
    : undefined;

export const fromDraftDate = (value) => {
  if (typeof value !== 'string') return undefined;
  const date = moment(value, 'YYYY-MM-DD', true);
  // невалидная дата роняет react-datepicker
  return date.isValid() ? date.toDate() : undefined;
};

// Аватар и свидетельство нужны пройденному шагу, любой файл — если он был
// загружен (на него есть ссылка).
const missingFiles = (body, completed, fileRefs) =>
  FILE_KEYS.filter(
    (key) =>
      (!!fileRefs[key] ||
        (key !== 'inclusiveness_document' && completed[FILE_STEPS[key]])) &&
      !(body[key] instanceof Blob),
  );

const earliestStep = (keys) => Math.min(...keys.map((key) => FILE_STEPS[key]));

// Шаг, на который надо вернуться за пропавшим файлом, или null.
export const firstMissingFileStep = (body, completed, fileRefs) => {
  const missing = missingFiles(body, completed, fileRefs);
  return missing.length ? earliestStep(missing) : null;
};

// Файлы из IndexedDB могли не восстановиться (хранилище недоступно, запись
// удалили). Без них нельзя оставаться на шагах после их загрузки: apply()
// упал бы на FormData.append.
export const repairProgress = (draft) => {
  const missing = missingFiles(draft.body, draft.completed, draft.fileRefs);
  if (!missing.length) return { draft, filesLost: false };

  const step = earliestStep(missing);
  const fileRefs = { ...draft.fileRefs };
  missing.forEach((key) => delete fileRefs[key]);
  return {
    draft: {
      ...draft,
      fileRefs,
      // шаг перед step остаётся пройденным, так что его вкладка доступна
      completed: draft.completed.map((done, i) => done && i < step),
      selectedIndex: Math.min(draft.selectedIndex, step),
    },
    filesLost: true,
  };
};

const openDb = () =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () =>
      request.result.createObjectStore(STORE).createIndex('savedAt', 'savedAt');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('IndexedDB is blocked'));
  });

// Соединение — на одну операцию: на iOS открытое соединение рвётся, пока
// вкладка в фоне (например, пока выбирают фото).
const withStore = (mode, operation) =>
  openDb().then((db) =>
    new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      // ошибки квоты приходят как abort транзакции
      const fail = () =>
        reject(transaction.error ?? new Error('IndexedDB transaction failed'));
      transaction.onerror = fail;
      transaction.onabort = fail;
      const result = operation(transaction.objectStore(STORE));
      transaction.oncomplete = () => resolve(result);
    }).finally(() => db.close()),
  );

const newFileId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

export const putDraftFile = async (file) => {
  // ArrayBuffer, а не сам File: Safari в части режимов не сохраняет Blob
  const data = await new Response(file).arrayBuffer();
  const id = newFileId();
  await withStore('readwrite', (store) => {
    store.put(
      {
        savedAt: Date.now(),
        name: file.name,
        type: file.type,
        lastModified: file.lastModified,
        data,
      },
      id,
    );
  });
  return id;
};

const getDraftFiles = (fileRefs) =>
  withStore('readonly', (store) => {
    const files = {};
    Object.entries(fileRefs).forEach(([key, id]) => {
      const request = store.get(id);
      request.onsuccess = () => {
        const record = request.result;
        if (record?.data) {
          files[key] = new File([record.data], record.name, {
            type: record.type,
            lastModified: record.lastModified,
          });
        }
      };
    });
    return files;
  });

// { avatar_photo: File, ... }; не нашедшихся файлов в ответе нет.
export const readDraftFiles = (fileRefs) => {
  if (!Object.keys(fileRefs).length) return Promise.resolve({});
  const timeout = new Promise((resolve) =>
    setTimeout(() => resolve({}), RESTORE_TIMEOUT),
  );
  return Promise.race([getDraftFiles(fileRefs), timeout]).catch(() => ({}));
};

export const deleteDraftFiles = (ids) => {
  const existing = ids.filter(Boolean);
  if (!existing.length) return Promise.resolve();
  return withStore('readwrite', (store) =>
    existing.forEach((id) => store.delete(id)),
  ).catch(() => {});
};

// Файлы вкладок, закрытых без отправки заявки. keepIds — файлы этой вкладки.
export const purgeDraftFiles = (keepIds) =>
  withStore('readwrite', (store) => {
    // курсор по ключам: сами файлы (мегабайты) не читаем
    const request = store
      .index('savedAt')
      .openKeyCursor(IDBKeyRange.upperBound(Date.now() - STALE_FILE_AGE));
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;
      if (!keepIds.includes(cursor.primaryKey)) store.delete(cursor.primaryKey);
      cursor.continue();
    };
  }).catch(() => {});

// fileIds — файлы из памяти страницы; файлы сохранённого черновика
// удаляются и без них.
export const clearDraft = (fileIds = []) => {
  const stored = loadDraft();
  deleteDraftFiles([...fileIds, ...Object.values(stored?.fileRefs ?? {})]);
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch (e) {
    // см. saveDraft
  }
  clearStepDrafts();
};
